import { Checkbox, Modal } from 'antd'
import type { CheckboxChangeEvent } from 'antd/es/checkbox'
import classNames from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Control,
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import AtomFormInput from '@/components/Atoms/AtomFormInput/AtomFormInput'
import AtomSelect from '@/components/Atoms/AtomSelect/AtomSelect'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import Loader from '@/components/Atoms/Loader/Loader'
import { FIELD_PERMISSION_MAP, NULL_UUID_ID, OWNER_ROLES } from '@/constants'
import { OrganisationEditType } from '@/pages/OrganisationSettings/OrganisationSettings.config'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type {
  FormInputControlType,
  FormInputNameType,
  FormInputRegisterType,
  OptionType,
  OrganisationFieldPermissions,
  OrganisationInterface,
  SelectFormControlType,
} from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { hasRoleFromGroup } from '@/utils/auth/roleGroups'
import { isSupplierStaff } from '@/utils/auth/userRoles'
import { withRequiredOrganisationFields } from '@/utils/organisation/withRequiredOrganisationFields'

import { ORGANISATION_DATA_EDIT_FORM_FIELDS } from './Data.config'
import cn from './Data.module.scss'

type DataPropsType = {
  errors: FieldErrors
  register: UseFormRegister<OrganisationEditType>
  getValues: UseFormGetValues<OrganisationEditType>
  control: Control<OrganisationEditType>
  setValue: UseFormSetValue<OrganisationEditType>
}

interface SupervisorCandidate {
  id: string
  enabled?: boolean
  description?: string
  name?: string
  login?: string
}

const PAGE_SIZE = 40
const WIDE_FIELD_NAMES = new Set([
  'fullLegalName',
  'legalAddress',
  'postalAddress',
])
const DATA_SECTIONS = [
  {
    id: 'organisation',
    eyebrow: 'Основное',
    title: 'Реквизиты организации',
    hint: 'Идентификаторы и наименования компании.',
    items: ['description', 'vatNumber', 'shortLegalName', 'fullLegalName'],
  },
  {
    id: 'addresses',
    eyebrow: 'Локация',
    title: 'Адреса',
    hint: 'Юридический и почтовый адреса организации.',
    items: ['legalAddress', 'postalAddress'],
  },
  {
    id: 'contacts',
    eyebrow: 'Связь',
    title: 'Контактные данные',
    hint: 'Каналы связи и адрес для рассылки.',
    items: ['phoneNumber', 'email', 'emailDistribution'],
  },
  {
    id: 'documents',
    eyebrow: 'Документы',
    title: 'Подписание и регистрация',
    hint: 'Даты регистрации и сведения о подписанте.',
    items: [
      'registrationDate',
      'ipRegistrationDate',
      'signatoryFullName',
      'signatoryOperatesUnder',
      'signatoryPosition',
      'supervisorManager',
    ],
  },
  {
    id: 'banking',
    eyebrow: 'Финансы',
    title: 'Банковские реквизиты',
    hint: 'Основной счёт организации для работы в системе.',
    items: ['mainBankAccount'],
  },
] as const

function Data({ errors, register, control, setValue }: DataPropsType) {
  const [supervisorManagers, setSupervisorManagers] = useState<OptionType[]>([])

  const managersPageRef = useRef(0)
  const hasMoreManagersRef = useRef(true)
  const loadingManagersRef = useRef(false)
  const hasInitiallyFetchedRef = useRef(false)

  const [loadingManagers, setLoadingManagers] = useState(false)

  const [org, setOrg] = useState({} as OrganisationInterface)
  const [isOrgLegal, setOrgLegal] = useState(false)
  const [bankAccounts, setBankAccounts] = useState<OptionType[]>([])

  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false)
  const hasFetchedBankAccountsRef = useRef(false)

  const [permissions, setPermissions] =
    useState<OrganisationFieldPermissions | null>(null)
  const [permissionsLoading, setPermissionsLoading] = useState(true)

  const {
    tokenRoles,
    selectedOrganisation,
    organisation,
    userOrganisation,
    setSelectedOrganisation,
    setOrganisation,
    setOrganisationPermissions,
  } = useUserInfoStore((state) => state)

  const canEditManager = hasRoleFromGroup(tokenRoles, 'SENIOR_MANAGERS')

  const { code } = useParams()
  const isOrganisationSelected =
    Boolean(selectedOrganisation.code) &&
    String(selectedOrganisation.code) === code

  const [modal, contextHolder] = Modal.useModal()

  const fetchBankAccounts = useCallback(async () => {
    if (!org.id || hasFetchedBankAccountsRef.current) return

    setLoadingBankAccounts(true)
    try {
      const response = await api.get(`/organisations/${org.id}/bank-accounts`)
      const accounts = response.data.bankAccounts || response.data || []

      const options: OptionType[] = accounts.map(
        (acc: { id: string; iban: string }) => ({
          value: acc.id,
          label: acc.iban,
        })
      )

      setBankAccounts((prev) => {
        const existingIds = new Set(prev.map((p) => p.value))
        const newOptions = options.filter((opt) => !existingIds.has(opt.value))
        return [...prev, ...newOptions]
      })

      hasFetchedBankAccountsRef.current = true
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setLoadingBankAccounts(false)
    }
  }, [org.id, modal])

  useEffect(() => {
    if (!code) return

    setPermissionsLoading(true)

    const targetOrg =
      selectedOrganisation.code && String(selectedOrganisation.code) === code
        ? selectedOrganisation
        : organisation.code && String(organisation.code) === code
          ? organisation
          : null

    Promise.all([
      api.get(`/organisations/${code}`),
      api.get(`/organisations/${targetOrg!.id}/permissions`),
    ])
      .then(([orgResponse, permissionsResponse]) => {
        const orgData: OrganisationInterface =
          orgResponse.data.organisation || orgResponse.data

        setOrg(orgData)
        setOrgLegal(orgData.isLegal)

        const perms =
          permissionsResponse.data.permissions || permissionsResponse.data

        const fields = perms.fields || perms

        setPermissions(fields)
        setOrganisationPermissions(fields)

        if (
          selectedOrganisation.code &&
          String(selectedOrganisation.code) === code
        ) {
          setSelectedOrganisation(orgData)
        } else if (organisation.code && String(organisation.code) === code) {
          setOrganisation(orgData)
        }
      })
      .catch((error) => {
        handleApiError(error, modal)
      })
      .finally(() => {
        setPermissionsLoading(false)
      })
  }, [code])

  useEffect(() => {
    if (!org.code) return

    managersPageRef.current = 0
    hasMoreManagersRef.current = true
    loadingManagersRef.current = false
    hasInitiallyFetchedRef.current = false
    setSupervisorManagers([])

    hasFetchedBankAccountsRef.current = false
    setBankAccounts([])
  }, [org.code])

  useEffect(() => {
    if (!org.mainBankAccountShortModel) return
    setBankAccounts([
      {
        value: org.mainBankAccountShortModel.id,
        label: org.mainBankAccountShortModel.iban || '',
      },
    ])
  }, [org.mainBankAccountShortModel])

  const handleClickReadOnlyInput = () => {
    toast.warning('Нет прав на изменение', {
      description: 'У вас нет прав на изменение этой информации',
    })
  }

  const fetchSupervisorManagers = useCallback(
    async (pageNumber: number) => {
      if (loadingManagersRef.current || !org.code || !userOrganisation?.code)
        return

      loadingManagersRef.current = true
      setLoadingManagers(true)

      try {
        const response = await api.get('/users', {
          params: {
            organisationCode: userOrganisation.code,
            page: pageNumber,
            pageSize: PAGE_SIZE,
          },
        })

        const usersData = response.data.users || response.data
        const content = usersData.content || []
        const totalPages = usersData.totalPages || 0

        const options: OptionType[] = content
          .filter((user: SupervisorCandidate) => user.enabled === true)
          .map((user: SupervisorCandidate) => ({
            value: user.id,
            label: user.description || user.name || user.login,
          }))

        setSupervisorManagers((prev) => {
          if (pageNumber === 0) return options
          const existingIds = new Set(prev.map((item) => item.value))
          const uniqueNewOptions = options.filter(
            (opt) => !existingIds.has(opt.value)
          )
          return [...prev, ...uniqueNewOptions]
        })

        hasMoreManagersRef.current = pageNumber < totalPages - 1
        managersPageRef.current = pageNumber + 1
      } catch (error) {
        handleApiError(error, modal)
      } finally {
        loadingManagersRef.current = false
        setLoadingManagers(false)
      }
    },
    [org.code, userOrganisation?.code, modal]
  )

  const makeInputReadOnly = (fieldName: string): boolean => {
    if (!permissions) return true
    const permissionKey = FIELD_PERMISSION_MAP[fieldName]
    if (!permissionKey) return true
    return !permissions[permissionKey]
  }

  const handleActivateOrganisation = (e: CheckboxChangeEvent) => {
    const newValue = e.target.checked

    if (
      [
        OWNER_ROLES.ADMIN,
        OWNER_ROLES.SUPERMANAGER,
        OWNER_ROLES.MANAGER,
        OWNER_ROLES.SALESMAN,
      ].some((role) => tokenRoles.includes(role))
    ) {
      setOrgLegal(newValue)
      const {
        id,
        code,
        supervisorManagerShortModel,
        mainBankAccountShortModel,
        roles,
        ...rest
      } = org

      const payload = withRequiredOrganisationFields(
        {
          ...rest,
          supervisorManager: supervisorManagerShortModel
            ? supervisorManagerShortModel.id
            : NULL_UUID_ID,
          mainBankAccount: mainBankAccountShortModel
            ? mainBankAccountShortModel.id
            : NULL_UUID_ID,
          isLegal: newValue,
        },
        org
      )

      const loadingToast = toast.loading(
        newValue ? 'Активация организации...' : 'Деактивация организации...'
      )

      api
        .put(`/organisations/${org.code}`, payload, { _skipSanitize: true })
        .then((response) => {
          toast.success(
            newValue
              ? 'Организация активирована'
              : 'Организация деактивирована',
            {
              id: loadingToast,
              description: org.description,
            }
          )

          const updatedOrg = response.data.organisation || response.data

          if (updatedOrg.code == organisation.code) {
            setOrganisation(updatedOrg)
          } else {
            setSelectedOrganisation(updatedOrg)
          }
        })
        .catch((e) => {
          toast.dismiss(loadingToast)
          setOrgLegal(!newValue)
          handleApiError(e, modal)
        })
    } else {
      toast.warning('Нет прав на редактирование', {
        description: 'У вас нет прав на редактирование данной информации',
      })
    }
  }

  const handlePopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement
      const isNearBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight < 5

      if (
        isNearBottom &&
        hasMoreManagersRef.current &&
        !loadingManagersRef.current
      ) {
        fetchSupervisorManagers(managersPageRef.current)
      }
    },
    [fetchSupervisorManagers]
  )

  const handleManagerSelectHover = useCallback(() => {
    if (!hasInitiallyFetchedRef.current && org.code) {
      hasInitiallyFetchedRef.current = true
      fetchSupervisorManagers(0)
    }
  }, [org.code, fetchSupervisorManagers])

  const handleBankAccountSelectHover = useCallback(() => {
    fetchBankAccounts()
  }, [fetchBankAccounts])

  const formatFieldTitle = (title: string) => title.replace(/:$/, '')
  const fieldByName = Object.fromEntries(
    ORGANISATION_DATA_EDIT_FORM_FIELDS.map((field) => [field.name, field])
  )

  const renderFormFieldCard = (fieldName: string) => {
    const field = fieldByName[fieldName]

    if (!field) return null

    const { title, name, id } = field
    const isReadOnly = makeInputReadOnly(name)

    return (
      <div
        key={id}
        className={classNames(
          cn.fieldCard,
          WIDE_FIELD_NAMES.has(name) && cn.fieldCardWide
        )}
      >
        <div className={cn.fieldHeader}>
          <span className={cn.fieldLabel}>{formatFieldTitle(title)}</span>
          <span
            className={classNames(
              cn.fieldPermission,
              isReadOnly && cn.fieldPermissionReadonly
            )}
          >
            {isReadOnly ? 'Только чтение' : 'Редактируется'}
          </span>
        </div>

        <div
          className={classNames(
            cn.fieldControl,
            name === 'vatNumber' && cn.fieldControlWithAction
          )}
        >
          <AtomFormInput
            location="organisationEdit"
            layout="stacked"
            fullWidth
            isOrganisationSelected={isOrganisationSelected}
            name={name as FormInputNameType}
            errors={errors}
            register={register as FormInputRegisterType}
            setValue={setValue}
            handleInputKeyDown={
              name == 'vatNumber'
                ? (e) => {
                    if (
                      e.code === 'KeyE' ||
                      e.code === 'Equal' ||
                      e.code === 'Minus' ||
                      e.code === 'Period' ||
                      e.code === 'Comma'
                    )
                      e.preventDefault()
                  }
                : undefined
            }
            handleClickReadOnlyInput={
              isReadOnly ? handleClickReadOnlyInput : undefined
            }
            readOnly={isReadOnly}
            control={control as unknown as FormInputControlType}
            showExpandable={true}
          />
        </div>
      </div>
    )
  }

  const renderManagerCard = () => (
    <div
      key="supervisorManager"
      className={cn.fieldCard}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onDrag={(e) => e.preventDefault()}
      onDrop={(e) => e.preventDefault()}
      onMouseEnter={handleManagerSelectHover}
    >
      <div className={cn.fieldHeader}>
        <span className={cn.fieldLabel}>Менеджер</span>
        <span
          className={classNames(
            cn.fieldPermission,
            !canEditManager && cn.fieldPermissionReadonly
          )}
        >
          {!canEditManager ? 'Только чтение' : 'Редактируется'}
        </span>
      </div>

      <div className={cn.fieldControl}>
        <HoverElement>
          <AtomSelect
            options={supervisorManagers}
            formName="supervisorManager"
            formErrors={errors}
            formControl={control as SelectFormControlType}
            layout="stacked"
            fullWidth
            isOrganisationSelected={isOrganisationSelected}
            showSearch
            disabled={!canEditManager}
            loading={loadingManagers}
            onPopupScroll={handlePopupScroll}
          />
        </HoverElement>
      </div>
    </div>
  )

  const renderBankAccountCard = () => (
    <div
      key="mainBankAccount"
      className={classNames(cn.fieldCard, cn.fieldCardWide)}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onDrag={(e) => e.preventDefault()}
      onDrop={(e) => e.preventDefault()}
      onMouseEnter={handleBankAccountSelectHover}
    >
      <div className={cn.fieldHeader}>
        <span className={cn.fieldLabel}>Главный банковский счет</span>
        <span className={cn.fieldPermission}>Выбор из списка</span>
      </div>

      <div className={cn.fieldControl}>
        <HoverElement>
          <AtomSelect
            options={bankAccounts}
            formName="mainBankAccount"
            formErrors={errors}
            formControl={control as SelectFormControlType}
            layout="stacked"
            fullWidth
            isOrganisationSelected={isOrganisationSelected}
            loading={loadingBankAccounts}
          />
        </HoverElement>
      </div>
    </div>
  )

  const renderSectionItem = (itemName: string) => {
    if (itemName === 'supervisorManager') return renderManagerCard()
    if (itemName === 'mainBankAccount') return renderBankAccountCard()
    return renderFormFieldCard(itemName)
  }

  return (
    <>
      {contextHolder}
      {permissionsLoading ? (
        <div className={cn.loading}>
          <Loader size={20} paddingTop={32} />
        </div>
      ) : (
        <section
          className={classNames(
            cn.data,
            isOrganisationSelected && cn.organisationSelected
          )}
        >
          <div className={cn.inner}>
            <div className={cn.hero}>
              <div className={cn.heroCopy}>
                <span className={cn.eyebrow}>Настройки организации</span>
                <p className={cn.title}>Данные контрагента</p>
                <p className={cn.subtitle}>
                  Основные реквизиты, контактные данные и ответственные лица.
                  Поля доступны для редактирования в пределах ваших прав.
                </p>
              </div>

              <div className={cn.heroMeta}>
                {isSupplierStaff(tokenRoles) &&
                  Object.keys(selectedOrganisation).length > 0 &&
                  Object.keys(org).length > 0 &&
                  org.code == selectedOrganisation.code && (
                    <div className={cn.organisationActivateCheckbox}>
                      <span>Организация активирована</span>
                      <Checkbox
                        checked={isOrgLegal}
                        onChange={handleActivateOrganisation}
                      />
                    </div>
                  )}
                <div className={cn.metaCard}>
                  <span className={cn.metaCardLabel}>Код организации</span>
                  <span className={cn.metaCardValue}>{org.code || '—'}</span>
                </div>
              </div>
            </div>

            <div className={cn.sections}>
              {DATA_SECTIONS.map((section) => (
                <section key={section.id} className={cn.section}>
                  <div className={cn.sectionHeader}>
                    <span className={cn.sectionEyebrow}>{section.eyebrow}</span>
                    <div className={cn.sectionTitleRow}>
                      <h2 className={cn.sectionTitle}>{section.title}</h2>
                      <p className={cn.sectionHint}>{section.hint}</p>
                    </div>
                  </div>

                  <div className={cn.fieldsGrid}>
                    {section.items.map((item) => renderSectionItem(item))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  )
}

export default Data
